import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { showNoAccessNotification } from '@/functions/notifications';
import type { useSchoolStore } from '@/stores/school';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { AvailableParts } from '@/types/Certificate';

/**
 * Notify and redirect the user if they are not authorized to access the requested page.
 * Either redirect to the last or supplied route that the user had access to or to the home page.
 */
const notifyAndHandleNoAccess = async (to?: RouteLocationNormalized|RouteLocationRaw): Promise<RouteLocationRaw> => {
    showNoAccessNotification();

    return to || { name: 'default' };
};

/**
 * Attempt to retrieve the school from the passed in slug, or via session storage.
 * Otherwise, return the default school or the first school in the list, if the default school
 * is otherwise unavailable.
 */
const selectSchoolFromRoute = (
    schoolStore: ReturnType<typeof useSchoolStore>,
    schoolSlug?: string,
): FlightSchool|null => (
    schoolStore.getSchool(schoolSlug)
        ?? schoolStore.getSchoolByUrlSlug(import.meta.env.VITE_DEFAULT_SCHOOL_SLUG)
        ?? schoolStore.schools[0]
);

/**
 * Retrieve the course from the route, either by the given acronym or the first course.
 */
const selectCourseFromRoute = (
    school: FlightSchool,
    routeAcronym?: string,
    part?: AvailableParts,
): FlightSchoolCourse|undefined => {
    // If requested, try to return the course with the given acronym and part
    if (routeAcronym && part) {
        return school.courses.find(
            (schoolCourse: FlightSchoolCourse) => (
                schoolCourse.certificate.acronym.toLowerCase() === routeAcronym.toLowerCase()
                && schoolCourse.certificate.part === part
            ),
        ) ?? school.courses[0];
    }

    // if course acronym is not provided, return the course with the given part
    if (part) {
        return school.courses.find(
            (schoolCourse: FlightSchoolCourse) => (
                schoolCourse.certificate.part === part
            ),
        ) ?? school.courses[0];
    }

    // if no course was found, return the first course in the list
    return school.courses[0];
};

/**
 * Check if the current route has the same parameters as the previous route.
 */
const hasSameRouteParams = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    params: string[],
): boolean => params.every((param: string) => (
    to.params[param] && from.params[param] && to.params[param] === from.params[param]));

export {
    notifyAndHandleNoAccess, selectSchoolFromRoute, selectCourseFromRoute, hasSameRouteParams,
};
